/**
 * ═══════════════════════════════════════════════════════════════
 * SAT Generator 3.0 — API Client
 * Связь с Node.js Orchestrator (Express API)
 * Phase 8: Добавлены методы getMetrics, getMetricsJson, getPoolSummary
 * ═══════════════════════════════════════════════════════════════
 */

import type {
    Question,
    GenerateQuestionsRequest,
    GenerateQuestionsResponse,
    GetQuestionsResponse,
    HealthResponse,
    PoolStatsResponse,
    ReportResponse,
    MathDomain,
    MathDifficulty,
    ProgressSessionPayload,
    ProgressSummary,
    FeedbackVotePayload,
    FeedbackVoteResponse,
    SessionMistakesPayload,
    SessionReviewPayload,
    ActiveProgressSession,
    StartProgressSessionPayload,
    SaveSessionStepPayload,
    CompleteProgressSessionPayload,
} from './types';
import { fetchVerbalFastBatch } from './verbal-api';
import { readAuthToken, resolveTelemetryUserId } from './auth-session';

const API_BASE = '/api';

class ApiError extends Error {
    code: string;
    status: number;

    constructor(message: string, code: string, status: number) {
        super(message);
        this.name = 'ApiError';
        this.code = code;
        this.status = status;
    }
}

async function request<T>(
    endpoint: string,
    options: RequestInit = {},
): Promise<T> {
    const url = `${API_BASE}${endpoint}`;
    const token = readAuthToken();

    const response = await fetch(url, {
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...options.headers,
        },
        ...options,
    });

    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new ApiError(
            body.error || `HTTP ${response.status}`,
            body.code || 'UNKNOWN_ERROR',
            response.status,
        );
    }

    return response.json();
}

// ── Public API ──

export const api = {
    /** Health-check */
    health(): Promise<HealthResponse> {
        return request<HealthResponse>('/health');
    },

    /** PR-D4-035 — exchange Google ID token for app session JWT + user */
    googleAuth(credential: string): Promise<{
        success: true;
        token: string;
        user: {
            id: string;
            email: string;
            name: string | null;
            picture: string | null;
            role: string;
        };
    }> {
        return request('/auth/google', {
            method: 'POST',
            body: JSON.stringify({ credential }),
        });
    },

    /** Генерация пакета вопросов через полный пайплайн */
    generateQuestions(
        params: GenerateQuestionsRequest,
    ): Promise<GenerateQuestionsResponse> {
        return request<GenerateQuestionsResponse>('/generate-questions', {
            method: 'POST',
            body: JSON.stringify(params),
        });
    },

    /** Получение готовых вопросов из пула БД (мгновенный SELECT) — Math/orchestrator */
    getQuestions(
        topic: string,
        difficulty: string,
        studentId: string,
        count: number = 10,
    ): Promise<GetQuestionsResponse> {
        const params = new URLSearchParams({
            topic,
            difficulty,
            student_id: studentId,
            count: String(count),
        });
        return request<GetQuestionsResponse>(`/questions?${params}`);
    },

    /**
     * MadCrab_V Verbal pool fetch (port 8787 via Nginx `/api/` catch-all).
     * POST /api/questions/fast-batch — exact contract from MadCrab_V generateRwBatch.js
     */
    async getVerbalQuestionsFastBatch(
        categoryKey: string,
        difficulty: 'easy' | 'medium',
        count: number = 10,
    ): Promise<GetQuestionsResponse> {
        const questions = await fetchVerbalFastBatch(categoryKey, difficulty, count);
        return { questions, count: questions.length };
    },

    /** Получение вопросов из Math 3.0 API (по доменам SAT Math) */
    getMathQuestions(
        domain: MathDomain,
        difficulty: MathDifficulty,
        studentId: string,
        count: number = 10,
    ): Promise<GetQuestionsResponse> {
        const params = new URLSearchParams({
            domain,
            difficulty,
            student_id: studentId,
            count: String(count),
        });
        return request<GetQuestionsResponse>(`/math/questions?${params}`);
    },

    /** Получение одного вопроса по ID */
    getQuestionById(id: string): Promise<Question> {
        return request<Question>(`/questions/${id}`);
    },

    /** Статистика пула вопросов */
    getPoolStats(
        topic: string,
        difficulty: string,
    ): Promise<PoolStatsResponse> {
        const params = new URLSearchParams({ topic, difficulty });
        return request<PoolStatsResponse>(`/pool-stats?${params}`);
    },

    /** Жалоба на вопрос */
    reportQuestion(questionId: string): Promise<ReportResponse> {
        return request<ReportResponse>(`/questions/${questionId}/report`, {
            method: 'POST',
        });
    },

    // ── Phase 8: Monitoring & Pool Summary ──

    /** Получение метрик пайплайна (Prometheus text format) */
    getMetrics(): Promise<string> {
        return request<string>('/metrics', {
            headers: { Accept: 'text/plain' },
        });
    },

    /** Получение метрик в JSON */
    getMetricsJson(): Promise<{
        metrics: Record<string, unknown>;
        timestamp: string;
    }> {
        return request('/metrics/json');
    },

    /** Получение сводки по всем пулам (все темы/сложности) */
    getPoolSummary(): Promise<{
        pools: Array<{
            topicId: string;
            difficultyLevel: string;
            totalQuestions: number;
            publishedQuestions: number;
            minPoolSize: number;
            status: 'healthy' | 'warning' | 'critical';
        }>;
    }> {
        return request('/pool-summary');
    },

    // ── Phase 9: Testing Modes & Analytics ──

    startSession(params: {
        student_id: string;
        mode?: 'training' | 'full_practice' | 'spartan';
        topic_id?: string;
        tier?: 'T1' | 'T2' | 'T3';
        module_size?: number;
    }): Promise<import('./types').SessionStartResponse> {
        return request('/session/start', { method: 'POST', body: JSON.stringify(params) });
    },

    signalIdleTimeout(sessionId: string): Promise<{ session_id: string; status: string }> {
        return request(`/session/${sessionId}/idle-timeout`, { method: 'POST' });
    },

    getStudentOverview(
        studentId: string,
        mode?: string,
    ): Promise<import('./types').StudentOverview> {
        return request(`/student/${studentId}/overview?mode=${mode ?? 'training'}`);
    },

    getDomainBreakdown(
        studentId: string,
        mode?: string,
    ): Promise<{ domains: import('./types').DomainBreakdown[] }> {
        return request(`/student/${studentId}/domains?mode=${mode ?? 'training'}`);
    },

    getSkillBreakdown(
        studentId: string,
        domain: string,
        mode?: string,
    ): Promise<{ domain: string; skills: import('./types').SkillBreakdown[] }> {
        return request(`/student/${studentId}/skills?domain=${domain}&mode=${mode ?? 'training'}`);
    },

    getRecommendations(
        studentId: string,
        mode?: string,
    ): Promise<{ recommendations: import('./types').Recommendation[] }> {
        return request(`/student/${studentId}/recommendations?mode=${mode ?? 'training'}`);
    },

    getPracticeTests(
        studentId: string,
    ): Promise<{ tests: import('./types').PracticeTestHistory[] }> {
        return request(`/student/${studentId}/practice-tests`);
    },

    recordSessionAnswer(
        sessionId: string,
        questionId: string,
        selectedOptionIndex: number,
        timeSpentSeconds: number,
    ): Promise<{ session_id: string; answers_recorded: number }> {
        return request(`/session/${sessionId}/answer`, {
            method: 'POST',
            body: JSON.stringify({
                question_id: questionId,
                selected_option_index: selectedOptionIndex,
                time_spent_seconds: timeSpentSeconds,
            }),
        });
    },

    submitModule1(
        sessionId: string,
        answers: Array<{
            question_id: string;
            selected_option_index: number;
            time_spent_seconds: number;
        }>,
    ): Promise<Record<string, unknown>> {
        return request(`/session/${sessionId}/submit-module-1`, {
            method: 'POST',
            body: JSON.stringify({ answers }),
        });
    },

    saveProgressSession(
        payload: ProgressSessionPayload,
    ): Promise<{ ok: boolean; sessionId: string; answersInserted: number }> {
        return request('/progress/session', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    getProgressSummary(userId: string): Promise<ProgressSummary> {
        const params = new URLSearchParams({ userId });
        return request(`/progress/summary?${params}`);
    },

    getSessionMistakes(sessionId: string, userId: string): Promise<SessionMistakesPayload> {
        const params = new URLSearchParams({ userId });
        return request(`/progress/sessions/${encodeURIComponent(sessionId)}/mistakes?${params}`);
    },

    getSessionFull(sessionId: string, userId: string): Promise<SessionReviewPayload> {
        const params = new URLSearchParams({ userId });
        return request(`/progress/sessions/${encodeURIComponent(sessionId)}/full?${params}`);
    },

    deleteSession(sessionId: string): Promise<{ success: true; deletedSessionId: string }> {
        const params = new URLSearchParams({ userId: resolveTelemetryUserId() });
        return request(`/progress/sessions/${encodeURIComponent(sessionId)}?${params}`, {
            method: 'DELETE',
        });
    },

    startProgressSession(
        payload: StartProgressSessionPayload,
    ): Promise<{ sessionId: string; status: 'in_progress'; abandonedCount: number }> {
        return request('/progress/sessions/start', {
            method: 'POST',
            body: JSON.stringify({
                ...payload,
                userId: resolveTelemetryUserId(),
            }),
        });
    },

    saveSessionStep(
        sessionId: string,
        payload: SaveSessionStepPayload,
    ): Promise<{ ok: true; sessionId: string }> {
        return request(`/progress/sessions/${encodeURIComponent(sessionId)}/step`, {
            method: 'PATCH',
            body: JSON.stringify({
                ...payload,
                userId: resolveTelemetryUserId(),
            }),
        });
    },

    completeProgressSession(
        sessionId: string,
        payload: CompleteProgressSessionPayload,
    ): Promise<{ ok: true; sessionId: string; status: 'completed' }> {
        return request(`/progress/sessions/${encodeURIComponent(sessionId)}/complete`, {
            method: 'POST',
            body: JSON.stringify({
                ...payload,
                userId: resolveTelemetryUserId(),
            }),
        });
    },

    getActiveProgressSession(): Promise<{ session: ActiveProgressSession | null }> {
        const params = new URLSearchParams({ userId: resolveTelemetryUserId() });
        return request(`/progress/sessions/active?${params}`);
    },

    abandonProgressSession(
        sessionId: string,
    ): Promise<{ ok: true; sessionId: string; status: 'abandoned' }> {
        const params = new URLSearchParams({ userId: resolveTelemetryUserId() });
        return request(`/progress/sessions/${encodeURIComponent(sessionId)}/abandon?${params}`, {
            method: 'POST',
        });
    },

    voteQuestionFeedback(payload: FeedbackVotePayload): Promise<FeedbackVoteResponse> {
        return request('/feedback/vote', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },
};

export { ApiError };
export default api;
